/**
 * CreditNoteModal Component
 *
 * Modal dialog for creating credit notes against an invoice.
 * Supports different credit types and validates amount against balance due.
 *
 * User Story: US-REC-001 - Invoice Reconciliation
 */

import { useState, useCallback, useEffect } from 'react'
import { toLocalDateString } from '../../utils/dateUtils'
import { useCreditNotes, CREDIT_NOTE_TYPES } from '@/hooks/useCreditNotes'
import { useAutoClearInput } from '@/hooks/useAutoClearInput'
import { LegacyInvoiceBanner } from '@/components/invoices/LegacyInvoiceBanner'
import { XeroSyncStatusIndicator } from './XeroSyncStatusIndicator'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { DatePicker } from '@/components/ui/date-picker'
import {
  AlertCircleIcon,
  CheckCircleIcon,
  RefreshCwIcon
} from 'lucide-react'

/**
 * Format currency value for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(amount)
}

/**
 * CreditNoteModal provides a form for creating credit notes
 * @param {Object} props - Component props
 * @param {Object} props.invoice - Invoice data
 * @param {string} props.invoice.id - Invoice ID
 * @param {string} props.invoice.invoice_no - Invoice number
 * @param {string} props.invoice.customer_id - Customer ID
 * @param {string} props.invoice.customer_name - Customer name
 * @param {number} props.invoice.amount_due - Amount due on invoice
 * @param {boolean} props.invoice.is_legacy_paid - Whether this is a legacy paid invoice
 * @param {boolean} props.open - Whether the modal is open
 * @param {function} props.onOpenChange - Callback when open state changes
 * @param {function} props.onSuccess - Callback on successful creation
 */
export function CreditNoteModal({ invoice, open, onOpenChange, onSuccess }) {
  const { createCreditNote, retrySyncToXero, isLoading, error: hookError } = useCreditNotes({
    invoiceId: invoice?.id,
    autoFetch: false
  })

  const [formData, setFormData] = useState({
    type: 'write_off',
    amount: '',
    reason: '',
    date: new Date(),
    notes: ''
  })
  const [validationError, setValidationError] = useState(null)
  const [submitError, setSubmitError] = useState(null)

  // Post-creation state to show sync status
  const [createdCreditNote, setCreatedCreditNote] = useState(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [retrying, setRetrying] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (open && invoice) {
      setFormData({
        type: invoice.is_legacy_paid ? 'legacy_settlement' : 'write_off',
        amount: invoice.amount_due?.toFixed(2) || '0.00',
        reason: '',
        date: new Date(),
        notes: ''
      })
      setValidationError(null)
      setSubmitError(null)
      setCreatedCreditNote(null)
      setShowSuccess(false)
    }
  }, [open, invoice])

  // Auto-clear input hook for amount field
  const {
    handleFocus: handleAmountFocus,
    handleBlur: handleAmountBlur,
    handleChange: handleAmountInputChange
  } = useAutoClearInput(
    '0.00',
    useCallback((value) => {
      setFormData((prev) => ({ ...prev, amount: value }))
    }, [])
  )

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setValidationError(null)
  }

  const handleUseFullBalance = () => {
    if (invoice?.amount_due) {
      setFormData((prev) => ({
        ...prev,
        amount: invoice.amount_due.toFixed(2)
      }))
    }
  }

  const validateForm = () => {
    const amount = parseFloat(formData.amount)

    if (!formData.type) {
      setValidationError('Please select a credit type')
      return false
    }

    if (isNaN(amount) || amount <= 0) {
      setValidationError('Please enter a valid amount')
      return false
    }

    // For non-refund types, amount cannot exceed amount_due
    if (formData.type !== 'refund' && amount > invoice?.amount_due) {
      setValidationError(
        `Amount cannot exceed the balance due (${formatCurrency(invoice?.amount_due)})`
      )
      return false
    }

    if (!formData.reason || formData.reason.trim() === '') {
      setValidationError('Please provide a reason for this credit note')
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitError(null)

    if (!validateForm()) {
      return
    }

    try {
      const result = await createCreditNote({
        customer_id: invoice.customer_id,
        invoice_id: invoice.id,
        amount: parseFloat(formData.amount),
        reason: formData.reason.trim(),
        type: formData.type,
        date: toLocalDateString(formData.date),
        notes: formData.notes?.trim() || null
      })

      // Store created credit note and show success state
      setCreatedCreditNote(result.credit_note)
      setShowSuccess(true)

      // Notify parent of success
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (err) {
      setSubmitError(err.message)
    }
  }

  // Handle retry sync
  const handleRetrySync = async () => {
    if (!createdCreditNote?.id) return

    setRetrying(true)
    try {
      const result = await retrySyncToXero(createdCreditNote.id)
      // Update local state with new sync status
      setCreatedCreditNote(prev => ({
        ...prev,
        xero_sync_status: result.credit_note?.xero_sync_status || 'synced',
        xero_sync_error: result.credit_note?.xero_sync_error || null,
        xero_credit_note_number: result.credit_note?.xero_credit_note_number || null
      }))
    } catch (err) {
      console.error('[CreditNoteModal] Retry failed:', err)
    } finally {
      setRetrying(false)
    }
  }

  // Handle closing after success
  const handleClose = () => {
    onOpenChange(false)
  }

  // Filter credit note types based on invoice type
  const availableTypes = invoice?.is_legacy_paid
    ? CREDIT_NOTE_TYPES.filter((t) => t.value === 'legacy_settlement')
    : CREDIT_NOTE_TYPES.filter((t) => t.value !== 'legacy_settlement')

  const displayError = validationError || submitError || hookError

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {showSuccess ? 'Credit Note Created' : 'Create Credit Note'}
          </DialogTitle>
          <DialogDescription>
            {showSuccess
              ? `Credit note ${createdCreditNote?.credit_note_no} has been created`
              : `Create a credit note for invoice ${invoice?.invoice_no}`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Success State */}
        {showSuccess && createdCreditNote && (
          <div className="space-y-4">
            {/* Success message */}
            <div className="rounded-md bg-green-50 border border-green-200 p-4">
              <div className="flex items-center gap-3">
                <CheckCircleIcon className="h-5 w-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-800">
                    Credit note created successfully
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    {createdCreditNote.credit_note_no} for {formatCurrency(createdCreditNote.amount)}
                  </p>
                </div>
              </div>
            </div>

            {/* Xero Sync Status */}
            {createdCreditNote.xero_sync_status &&
             createdCreditNote.xero_sync_status !== 'not_applicable' && (
              <div className="rounded-md border p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Xero Sync Status
                </h4>
                <div className="flex items-center justify-between">
                  <XeroSyncStatusIndicator
                    status={createdCreditNote.xero_sync_status}
                    syncError={createdCreditNote.xero_sync_error}
                    xeroNumber={createdCreditNote.xero_credit_note_number}
                    size="md"
                    showLabel
                  />
                  {createdCreditNote.xero_sync_status === 'failed' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRetrySync}
                      disabled={retrying}
                    >
                      <RefreshCwIcon
                        className={`h-4 w-4 mr-2 ${retrying ? 'animate-spin' : ''}`}
                      />
                      {retrying ? 'Retrying...' : 'Retry Sync'}
                    </Button>
                  )}
                </div>
                {createdCreditNote.xero_sync_status === 'synced' &&
                 createdCreditNote.xero_credit_note_number && (
                  <p className="text-xs text-gray-500 mt-2">
                    Xero Reference: {createdCreditNote.xero_credit_note_number}
                  </p>
                )}
                {createdCreditNote.xero_sync_status === 'failed' &&
                 createdCreditNote.xero_sync_error && (
                  <p className="text-xs text-red-600 mt-2">
                    Error: {createdCreditNote.xero_sync_error}
                  </p>
                )}
              </div>
            )}

            <DialogFooter>
              <Button onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Creation Form */}
        {!showSuccess && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Invoice Summary */}
          <div className="rounded-md bg-gray-50 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Invoice</span>
              <span className="font-medium">{invoice?.invoice_no}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">Customer</span>
              <span className="font-medium">{invoice?.customer_name}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600">Balance Due</span>
              <span className="font-semibold text-amber-600">
                {formatCurrency(invoice?.amount_due || 0)}
              </span>
            </div>
          </div>

          {/* Legacy Invoice Warning */}
          {invoice?.is_legacy_paid && (
            <LegacyInvoiceBanner compact />
          )}

          {/* Credit Type */}
          <div className="space-y-2">
            <Label htmlFor="credit-type">Credit Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => handleInputChange('type', value)}
            >
              <SelectTrigger id="credit-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Amount */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="credit-amount">Amount</Label>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={handleUseFullBalance}
              >
                Use full balance
              </Button>
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                &pound;
              </span>
              <Input
                id="credit-amount"
                type="number"
                step="0.01"
                min="0.01"
                max={invoice?.amount_due || undefined}
                value={formData.amount}
                onChange={handleAmountInputChange}
                onFocus={handleAmountFocus}
                onBlur={(e) => handleAmountBlur(e, '0.00')}
                className="pl-7"
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {/* Reason (required) */}
          <div className="space-y-2">
            <Label htmlFor="credit-reason">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="credit-reason"
              value={formData.reason}
              onChange={(e) => handleInputChange('reason', e.target.value)}
              placeholder="Enter the reason for this credit note..."
              rows={2}
              required
            />
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label>Date</Label>
            <DatePicker
              date={formData.date}
              onDateChange={(date) => handleInputChange('date', date)}
              placeholder="Select date"
            />
          </div>

          {/* Additional Notes (optional) */}
          <div className="space-y-2">
            <Label htmlFor="credit-notes">Additional Notes</Label>
            <Textarea
              id="credit-notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional notes (optional)..."
              rows={2}
            />
          </div>

          {/* Error Display */}
          {displayError && (
            <div
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              role="alert"
            >
              <AlertCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{displayError}</span>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create Credit Note'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CreditNoteModal
